import { gql, useQuery } from "@apollo/client";
import React from "react";
import { GetImagesQuery, ImageFilterInput } from "../../gql/graphql";
import { ShareparkDateTime } from "../../components/LocalTime";
import ShareParkTable, {
  usePaginationAndFilters,
} from "../../components/table/ShareParkTable";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";

const ALL_IMAGES = gql`
  query GetImages(
    $where: ImageFilterInput
    $skip: Int
    $take: Int
    $orderBy: [ImageSortInput!]
  ) {
    images(where: $where, take: $take, skip: $skip, order: $orderBy) {
      items {
        id
        url
        imageUrl
        capturedUtc
        siteId
        site {
          name
        }
        bayId
        bay {
          name
        }
        accessLaneId
        accessLane {
          name
        }
      }
      totalCount
    }
  }
`;

type ImageType = NonNullable<
  NonNullable<NonNullable<GetImagesQuery["images"]>["items"]>[number]
>;

const columnHelper = createColumnHelper<ImageType>();

const columns = [
  columnHelper.accessor("capturedUtc", {
    enableColumnFilter: true,
    header: "Captured",
    cell: (info) => {
      return <ShareparkDateTime timestamp={info.getValue()} />;
    },
  }),
  columnHelper.accessor("site.name", {
    header: "Site",
    // Filter: SelectColumnFilter,
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <Link to={"/sites/" + info.row.original.siteId}>
          {info.renderValue()}
        </Link>
      ) : (
        <>no site</>
      );
    },
  }),
  columnHelper.accessor("bay", {
    enableColumnFilter: false,
    enableSorting: false,
    header: "Bay",
    cell: (info) => {
      const bay = info.getValue();
      return bay?.name ?? "-";
    },
  }),
  columnHelper.accessor("accessLane", {
    enableColumnFilter: false,
    enableSorting: false,
    header: "Access Lane",
    cell: (info) => {
      const accessLane = info.getValue();
      return accessLane?.name ?? "-";
    },
  }),
  columnHelper.accessor("imageUrl", {
    enableColumnFilter: false,
    enableSorting: false,
    header: "Image",
    cell: (info) => {
      const url = info.getValue();
      return url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt="Captured"
            style={{ maxWidth: "150px", maxHeight: "100px" }}
          />
        </a>
      ) : (
        "-"
      );
    },
  }),
];

const Images: React.FC = () => {
  const { pagination, setPagination, columnFilters, setColumnFilters } =
    usePaginationAndFilters();

  // Add sorting state
  const [sorting, setSorting] = React.useState([
    {
      id: "capturedUtc", // Default sorting column
      desc: true, // Default sorting direction (descending)
    },
  ]);

  const where = React.useMemo(() => {
    const where: ImageFilterInput = {};

    for (const filter of columnFilters) {
      switch (filter.id) {
        case "capturedUtc":
          if (filter.value != null) {
            where.capturedUtc = { eq: String(filter.value) };
          }
          break;
        case "site":
          if (filter.value != null) {
            where.site = { name: { contains: String(filter.value) } };
          }
          break;
      }
    }

    return where;
  }, [columnFilters]);

  const orderBy = React.useMemo(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      return {
        [sort.id]: sort.desc ? "DESC" : "ASC",
      };
    }
    return undefined;
  }, [sorting]);

  const { loading, error, data } = useQuery<GetImagesQuery>(ALL_IMAGES, {
    variables: {
      take: pagination.pageSize,
      skip: pagination.pageSize * pagination.pageIndex,
      where: where,
      orderBy: orderBy ? [orderBy] : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">Images</h1>
      </div>

      {loading && <p>Loading...</p>}

      {error && <p>Error: {error.message}</p>}

      {data && data.images && (
        <ShareParkTable
          columns={columns as ColumnDef<ImageType>[]}
          data={data.images ?? { items: [], totalCount: 0 }}
          pagination={pagination}
          setPagination={setPagination}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          sorting={sorting}
          setSorting={setSorting}
        />
      )}
    </>
  );
};

export default Images;